import React, { useEffect, useState } from "react";
import { X, Play, Film } from "lucide-react";
import { api } from "@/api/client";

// PM Feedback Batch 1, item 6 (2026-09) — Drive-hosted samples get a real in-portal proxy
// (never the raw Drive link, which 403s in an <img>/<video>). Fetched as a blob through the
// shared authenticated `api` instance (same pattern as AgencyAvatar.jsx) so the response's own
// Content-Type tells us image vs video — never guessed from the URL.
function useProxyBlob(url) {
  const [state, setState] = useState({ objectUrl: null, mime: null, loading: !!url });
  useEffect(() => {
    if (!url) { setState({ objectUrl: null, mime: null, loading: false }); return undefined; }
    let active = true;
    let blobUrl = null;
    setState({ objectUrl: null, mime: null, loading: true });
    api.get(url, { responseType: "blob", silent401: true }).then((r) => {
      if (!active) return;
      blobUrl = window.URL.createObjectURL(r.data);
      setState({ objectUrl: blobUrl, mime: r.data.type || null, loading: false });
    }).catch(() => { if (active) setState({ objectUrl: null, mime: null, loading: false }); });
    return () => { active = false; if (blobUrl) window.URL.revokeObjectURL(blobUrl); };
  }, [url]);
  return state;
}

// PM Feedback Batch 1, item 3 (2026-09) — Quick Look layout fix: every sample type (video,
// Drive-proxied, fetched image, or the no-preview placeholder) now renders inside the SAME
// fixed 16:9 box via the padding-top percentage technique (not the CSS `aspect-ratio`
// property, which older Safari/WebKit support inconsistently) — previously only the video
// branch had this, so an image sample's box size varied with that image's own native
// dimensions and the placeholder was a third, different size again. Same box, every time,
// every browser.
// 1b — Selected Work plays INSIDE the portal, never linking out. Video samples (YouTube/Vimeo)
// render a locked-down nocookie/dnt iframe; un-embeddables (Instagram/website) fall back to an
// enlarged image lightbox. No raw url, handle, platform name or click-through is ever shown.
export function WorkViewer({ sample, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const isDrive = sample?.media_kind === "drive" && !!sample?.proxy_url;
  const { objectUrl: driveUrl, mime: driveMime, loading: driveLoading } = useProxyBlob(isDrive ? sample.proxy_url : null);
  if (!sample) return null;
  const isVideo = sample.media_kind === "video" && sample.embed_url;
  const isDriveVideo = isDrive && (driveMime || "").startsWith("video/");
  const isPlayableVideo = isVideo || isDriveVideo;
  return (
    <div onClick={onClose} data-testid="agency-work-viewer"
      style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(0,0,0,.82)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "min(920px, 94vw)", maxHeight: "90vh" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ color: "#fff", display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            {sample.role_tag && <span className="ag-pill ag-pill-gold">{sample.role_tag}</span>}
            {(sample.category_tags || []).slice(0, 3).map((t, i) => <span key={i} className="ag-pill ag-pill-neutral">{t}</span>)}
          </div>
          <button onClick={onClose} data-testid="agency-work-viewer-close" aria-label="Close"
            style={{ color: "rgba(255,255,255,.7)", background: "none", border: "none", cursor: "pointer" }}><X size={22} /></button>
        </div>
        <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", background: "#111", borderRadius: 8, overflow: "hidden" }}>
          {isVideo ? (
            <iframe title="Selected work" src={sample.embed_url} data-testid="agency-work-iframe"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              referrerPolicy="strict-origin-when-cross-origin" allowFullScreen
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }} />
          ) : isDrive ? (
            driveLoading ? (
              <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "rgba(255,255,255,.5)", fontSize: 12 }}>Loading…</div>
            ) : !driveUrl ? (
              <div data-testid="agency-work-image" style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <Film size={40} color="#6b6b6b" style={{ marginBottom: 14 }} />
                <div style={{ color: "rgba(255,255,255,.85)", fontSize: 15 }}>{sample.role_tag || sample._fallbackLabel || "Selected work"}</div>
              </div>
            ) : isDriveVideo ? (
              <video src={driveUrl} controls autoPlay data-testid="agency-work-video"
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", background: "#000" }} />
            ) : (
              <img src={driveUrl} alt="" data-testid="agency-work-image"
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
            )
          ) : sample.thumbnail_url ? (
            <img src={sample.thumbnail_url} alt="" data-testid="agency-work-image"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
          ) : (
            <div data-testid="agency-work-image" style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <Film size={40} color="#6b6b6b" style={{ marginBottom: 14 }} />
              <div style={{ color: "rgba(255,255,255,.85)", fontSize: 15 }}>{sample.role_tag || sample._fallbackLabel || "Selected work"}</div>
              <div style={{ color: "rgba(255,255,255,.5)", fontSize: 12, marginTop: 6 }}>{(sample.category_tags || []).join(" · ")}</div>
            </div>
          )}
        </div>
        {!isPlayableVideo && (
          <p style={{ color: "rgba(255,255,255,.55)", fontSize: 12, marginTop: 10, textAlign: "center" }}>
            Full showreel available on request through your NJM producer.
          </p>
        )}
      </div>
    </div>
  );
}

// A single work thumbnail with a play affordance for video samples. Samples with no fetched still
// (external links link-preview couldn't capture) render a labelled film-strip placeholder rather
// than vanishing — the quick-look never reads as "no work".
export function WorkThumb({ sample, onOpen, label, width = 96, height = 60 }) {
  const isVideo = sample.media_kind === "video" && sample.embed_url;
  const isDrive = sample.media_kind === "drive" && !!sample.proxy_url;
  const { objectUrl: driveUrl, mime: driveMime } = useProxyBlob(isDrive ? sample.proxy_url : null);
  const isDriveVideo = isDrive && (driveMime || "").startsWith("video/");
  const thumbSrc = sample.thumbnail_url || (isDrive && !isDriveVideo ? driveUrl : null);
  const caption = sample.role_tag || label;
  return (
    <button onClick={() => onOpen({ ...sample, _fallbackLabel: label })} data-testid="agency-work-thumb"
      style={{ flex: "none", width, padding: 0, border: 0, background: "none", cursor: "pointer", textAlign: "left" }}>
      <div style={{ position: "relative", width, height, background: "#ececec", borderRadius: 6, overflow: "hidden", display: "grid", placeItems: "center" }}>
        {thumbSrc
          ? <img src={thumbSrc} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" decoding="async" />
          : <Film size={18} color="#9a9a9a" />}
        {(isVideo || isDriveVideo) && (
          <span style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "rgba(0,0,0,.28)" }}>
            <span style={{ display: "grid", placeItems: "center", width: 26, height: 26, borderRadius: 999, background: "rgba(255,255,255,.92)" }}>
              <Play size={13} color="#0C0C0C" fill="#0C0C0C" style={{ marginLeft: 1 }} />
            </span>
          </span>
        )}
      </div>
      {caption && <div className="ag-small" style={{ fontSize: 10.5, marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{caption}</div>}
    </button>
  );
}

// PM Feedback Batch 1, item 6 (2026-09) — "no playable samples" empty state: a member card
// whose Selected Work is empty, OR whose samples are all un-renderable (no thumbnail, not
// video, not a Drive proxy — i.e. would otherwise show a wall of blank placeholders), shows
// this line instead. Purely a display-layer check on fields the server already returns.
export function hasPlayableSamples(samples) {
  return (samples || []).some((s) => s.media_kind === "video" || s.media_kind === "drive" || s.thumbnail_url);
}
